import styled, { css, keyframes } from 'styled-components';

const speechChatBarsPulse = keyframes`
  0%,
  100% {
    transform: scaleY(0.3);
    opacity: 0.45;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
`;

interface ButtonProps {
  $variant: 'chat' | 'modal';
}

export const Button = styled.button<ButtonProps>`
  font-family: inherit;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $variant }) => ($variant === 'modal' ? '6px' : '0')};

  width: ${({ $variant }) => ($variant === 'chat' ? '24px' : 'auto')};
  height: ${({ $variant }) => ($variant === 'modal' ? '31px' : '24px')};
  padding: ${({ $variant }) => ($variant === 'modal' ? '0 12px' : '0')};

  background: ${({ $variant }) => ($variant === 'modal' ? '#2f80ed' : 'transparent')};

  border: ${({ $variant }) => ($variant === 'modal' ? '1px solid #2f80ed' : 'none')};

  color: ${({ $variant }) => ($variant === 'modal' ? '#fff' : '#ffffff')};

  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  span.material-icons {
    font-size: 16px;
  }

  ${({ $variant }) =>
    $variant === 'chat'
      ? css`
          flex-shrink: 0;
          min-width: 24px;
          min-height: 24px;

          &.speech-chat-audio-outline {
            position: relative;
            isolation: isolate;
            border: none;
            background: transparent;

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              border-radius: 4px;
              padding: 1px;
              box-sizing: border-box;
              background: linear-gradient(90deg, #007bff, #009f79);
              -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
              mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
              -webkit-mask-composite: xor;
              mask-composite: exclude;
              pointer-events: none;
            }

            &:hover::before,
            &:focus-visible::before {
              filter: brightness(1.08);
            }

            &:focus {
              outline: none;
            }

            &:focus-visible {
              outline: 2px solid rgba(0, 123, 255, 0.45);
              outline-offset: 2px;
            }

            &:disabled {
              opacity: 0.65;
            }

            &:disabled::before {
              filter: grayscale(0.25);
              opacity: 0.85;
            }

            .speech-chat-icon-slot .material-icons {
              position: relative;
              border: none;
              border-radius: 0;
              box-shadow: none;

              background: linear-gradient(90deg, #007bff, #009f79);
              background-clip: text;
              -webkit-background-clip: text;
              color: transparent;
              -webkit-text-fill-color: transparent;
            }

            .speech-chat-icon-slot .audio-loading-bars span:nth-child(1) {
              background: #007bff;
            }

            .speech-chat-icon-slot .audio-loading-bars span:nth-child(2) {
              background: #008b96;
            }

            .speech-chat-icon-slot .audio-loading-bars span:nth-child(3) {
              background: #009f79;
            }
          }

          .speech-chat-icon-slot {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            box-sizing: border-box;
          }

          .speech-chat-icon-slot .material-icons {
            font-size: 16px;
            width: 18px;
            height: 18px;
            line-height: 18px;
            padding: 0;
            margin: 0;
            border: 1px solid rgba(255, 255, 255, 0.9);
            border-radius: 3px;
            box-sizing: border-box;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .speech-chat-icon-slot .audio-loading-bars {
            height: 10px;
            gap: 2px;
            align-items: flex-end;
          }

          .speech-chat-icon-slot .audio-loading-bars span {
            width: 2px;
            height: 9px;
            flex-shrink: 0;
            align-self: flex-end;
            transform-origin: bottom center;
            border-radius: 1px;
          }

          .speech-chat-icon-slot .audio-loading-bars span:nth-child(1) {
            animation: ${speechChatBarsPulse} 1s ease-in-out 0s infinite;
          }

          .speech-chat-icon-slot .audio-loading-bars span:nth-child(2) {
            animation: ${speechChatBarsPulse} 1s ease-in-out 0.16s infinite;
          }

          .speech-chat-icon-slot .audio-loading-bars span:nth-child(3) {
            animation: ${speechChatBarsPulse} 1s ease-in-out 0.32s infinite;
          }
        `
      : ''}

  .audio-loading-bars span {
    background: #ffffff;
  }

  span.loading {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
